import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useUser } from "./lib/auth";
import { Layout } from "./components/Layout";
import { ArtifactsPage } from "./pages/ArtifactsPage";
import { AllArtifactsPage } from "./pages/AllArtifactsPage";
import { ArtifactVersionsPage } from "./pages/ArtifactVersionsPage";
import { IntegrationsPage } from "./pages/IntegrationsPage";

import { AdminLayout } from "./pages/admin/AdminLayout";
// OverviewPage pulls in recharts (~half of the entire JS bundle). Lazy-load
// so non-admin users — who never reach /admin — don't pay for it on first paint.
const OverviewPage = lazy(() =>
  import("./pages/admin/OverviewPage").then((m) => ({
    default: m.OverviewPage,
  })),
);
import { UsersPage } from "./pages/admin/UsersPage";
import { UsagePage } from "./pages/admin/UsagePage";
import { QuotasPage } from "./pages/admin/QuotasPage";
import { AuditLogPage } from "./pages/admin/AuditLogPage";
import { TokensPage } from "./pages/admin/TokensPage";
import { CdnAllowlistPage } from "./pages/admin/CdnAllowlistPage";
import { SharingSettingsPage } from "./pages/admin/SharingSettingsPage";
import { LifecyclePage } from "./pages/admin/LifecyclePage";
import { FeedbackPage as AdminFeedbackPage } from "./pages/admin/FeedbackPage";
import { AdminBigQueryPage } from "./pages/admin/AdminBigQueryPage";
import { AdminJiraPage } from "./pages/admin/jira/AdminJiraPage";
import { AlertsPage } from "./pages/admin/AlertsPage";
import { FeatureRequestsPage } from "./pages/FeatureRequestsPage";

/**
 * Root component: provides the AuthContext, defines top-level routes.
 *
 * Auth gating: every page calls /api/auth/me. If the call returns 401, the
 * fetcher redirects to /api/auth/google/login automatically — so the React
 * tree never has to handle "logged out" beyond a brief loading skeleton.
 */
export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<RequireAuth />}>
          <Route path="/" element={<Layout />}>
            <Route index element={<Navigate to="/artifacts" replace />} />
            <Route path="artifacts" element={<ArtifactsPage />} />
            {/* Legacy redirects so /mine and /shared bookmarks still work. */}
            <Route
              path="mine"
              element={<Navigate to="/artifacts?scope=mine" replace />}
            />
            <Route
              path="shared"
              element={
                <Navigate to="/artifacts?scope=all&visibility=shared" replace />
              }
            />
            <Route path="all-artifacts" element={<AllArtifactsPage />} />
            <Route
              path="artifacts/:slug/versions"
              element={<ArtifactVersionsPage />}
            />
            <Route path="feature-requests" element={<FeatureRequestsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />

            <Route path="admin" element={<AdminLayout />}>
              <Route
                index
                element={
                  <Suspense
                    fallback={<p className="text-slate-500">Loading…</p>}
                  >
                    <OverviewPage />
                  </Suspense>
                }
              />
              <Route path="users" element={<UsersPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="quotas" element={<QuotasPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="tokens" element={<TokensPage />} />
              <Route path="cdn-allowlist" element={<CdnAllowlistPage />} />
              <Route
                path="sharing-settings"
                element={<SharingSettingsPage />}
              />
              <Route path="lifecycle" element={<LifecyclePage />} />
              <Route path="feedback" element={<AdminFeedbackPage />} />
              <Route path="bigquery" element={<AdminBigQueryPage />} />
              <Route path="jira" element={<AdminJiraPage />} />
              <Route path="alerts" element={<AlertsPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}

import { Outlet } from "react-router-dom";

function RequireAuth() {
  const { user, loading } = useUser();
  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (!user) {
    // Fetcher should have redirected; this is a fallback.
    window.location.href = "/api/auth/google/login";
    return null;
  }
  return <Outlet />;
}
